/**
 * Pure helpers for the `urlSync` filters contract.
 *
 * No React imports: these are plain functions covering the URL/localStorage
 * resolution rules (all-or-nothing precedence, complete-state persistence, URL
 * budget guard). They are consumed by `ProviderWithUrlSync`.
 */

import type { CreateFiltersContextConfig, FilterValue } from "./types";

export const MAX_FILTERS_URL_LENGTH = 2000;

export const hasAnyParam = (
  params: URLSearchParams,
  urlKeys: readonly string[],
): boolean => urlKeys.some((key) => params.has(key));

const isPlainRecord = (value: unknown): value is Record<PropertyKey, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// A persisted value is trustworthy only if its runtime kind matches the default:
// same primitive `typeof`, or both arrays. Guards against a tampered localStorage
// smuggling e.g. a string where a `string[]` is expected.
//
// A `null` default is nullable and carries no non-null type hint (`typeof null`
// is "object"), so a persisted `number`/`string` would otherwise be dropped and
// the field could never be restored from storage. For null defaults we accept any
// concrete null/primitive/array value, but reject plain objects: no nullable
// filter field ever holds one at runtime, so an object smuggled from a tampered
// localStorage must not reach the state. `undefined` (absent key) is rejected too.
const hasSameKind = (candidate: unknown, reference: FilterValue): boolean => {
  if (reference === null) return candidate !== undefined && !isPlainRecord(candidate);
  return Array.isArray(reference)
    ? Array.isArray(candidate)
    : candidate !== undefined && typeof candidate === typeof reference;
};

/**
 * Reads persisted state and keeps only keys present in `initialState` whose
 * value kind also matches, so a tampered or malformed localStorage entry cannot
 * inject arbitrary keys or wrong-typed values into the filter state (and from
 * there into the caller's query builder). Mismatched keys fall back to default.
 */
export const readPersisted = <T extends Record<string, FilterValue>>(
  storageKey: string | undefined,
  initialState: T,
): Partial<T> | null => {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainRecord(parsed)) return null;

    const validated: Partial<T> = {};
    for (const key of Object.keys(initialState) as Array<keyof T>) {
      const candidate = parsed[key];
      if (hasSameKind(candidate, initialState[key])) {
        // Residual cast: narrowing `unknown` to a generic indexed type `T[keyof T]`
        // is unexpressible in TS; the runtime guard above makes it sound.
        validated[key] = candidate as T[keyof T];
      }
    }
    return validated;
  }
  catch {
    return null;
  }
};

export const writePersisted = <T>(
  storageKey: string | undefined,
  state: T,
  initialSerialized: string,
): void => {
  if (!storageKey) return;
  try {
    const serialized = JSON.stringify(state);
    if (serialized === initialSerialized) {
      localStorage.removeItem(storageKey);
      return;
    }
    localStorage.setItem(storageKey, serialized);
  }
  catch (error) {
    // Storage quota / disabled / non-serializable state — ignored in prod so a
    // failed persist never breaks the page, but surfaced in dev for debugging.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[urlSync] Failed to persist filters to localStorage.", error);
    }
  }
};

/**
 * Resolves the mount-time filter state with all-or-nothing precedence (D3):
 * a single known URL param wins outright over localStorage; otherwise the
 * persisted state is restored; otherwise the defaults.
 */
export const resolveInitialState = <T extends Record<string, FilterValue>>(
  config: Pick<CreateFiltersContextConfig<T>, "initialState" | "urlSync">,
  searchParams: URLSearchParams,
): T => {
  const { initialState, urlSync } = config;
  if (!urlSync) return initialState;

  if (hasAnyParam(searchParams, urlSync.urlKeys)) {
    try {
      return { ...initialState, ...urlSync.parse(searchParams) };
    }
    catch (error) {
      // A malformed deep-link (lone "%", bad token) or a buggy parse() must never
      // white-screen the mount — fall through to storage/defaults, for all consumers.
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("[urlSync] parse() threw on URL params; falling back to storage/defaults.", error);
      }
    }
  }

  const persisted = readPersisted(urlSync.storageKey, initialState);
  return persisted ? { ...initialState, ...persisted } : initialState;
};

/**
 * Enforces the URL budget (B4): beyond the limit the params are dropped whole —
 * never truncated partially — so localStorage stays the single source of truth.
 */
export const applyUrlBudget = (params: URLSearchParams): URLSearchParams => {
  if (params.toString().length <= MAX_FILTERS_URL_LENGTH) return params;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[urlSync] Serialized filters exceed ${MAX_FILTERS_URL_LENGTH} chars; `
      + "URL params dropped (localStorage still used).",
    );
  }
  return new URLSearchParams();
};
